import type {
  MachineAttachmentItemDto,
  MachineQuotationItemDto
} from '@api/machines'
import { formatPriceAndCurrency, TRANSPORT_OPTION_TYPES } from '@common/utils'

import {
  MolsonAccordion,
  MolsonBox,
  MolsonGrid,
  MolsonRadioButton,
  MolsonRadioGroup,
  MolsonTypography
} from '@components/reusable'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

type ServicesProps = {
  expanded?: boolean
  hasServiceContracts?: boolean
  machineQuotation: MachineAttachmentItemDto | MachineQuotationItemDto
  selectedTransportType?: string
  translationPrefix: string
}

export const Services = ({
  expanded = false,
  hasServiceContracts,
  machineQuotation,
  selectedTransportType,
  translationPrefix
}: ServicesProps) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState<boolean>(expanded)
  const { serviceContract } = machineQuotation as MachineQuotationItemDto
  const { transportOption, warrantyItem } = machineQuotation

  const { t } = useTranslation('', {
    keyPrefix: translationPrefix
  })

  return (
    <MolsonAccordion
      expanded={isSectionExpanded}
      onChange={() => setIsSectionExpanded(prev => !prev)}
      primaryText={t('services')}
    >
      <MolsonBox sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {warrantyItem && (
          <MolsonBox>
            <MolsonTypography sx={{ mb: 3.75 }} variant="h4">
              {t('warranty')}
            </MolsonTypography>
            <MolsonGrid container={true} spacing={1}>
              <MolsonGrid item={true} xs={6}>
                <MolsonRadioGroup
                  value={`${warrantyItem.cover} ${warrantyItem.hours} Hrs / ${
                    warrantyItem.months
                  } Months (+${formatPriceAndCurrency(warrantyItem.dnp)})`}
                >
                  <MolsonRadioButton
                    formControlLabelProps={{
                      label: `${warrantyItem.cover} ${
                        warrantyItem.hours
                      } Hrs / ${
                        warrantyItem.months
                      } Months (+${formatPriceAndCurrency(warrantyItem.dnp)})`
                    }}
                    radioBtnProps={{
                      disabled: true,
                      value: `${warrantyItem.cover} ${
                        warrantyItem.hours
                      } Hrs / ${
                        warrantyItem.months
                      } Months (+${formatPriceAndCurrency(warrantyItem.dnp)})`
                    }}
                    checked={true}
                  />
                </MolsonRadioGroup>
              </MolsonGrid>
            </MolsonGrid>
          </MolsonBox>
        )}
        {hasServiceContracts && (
          <MolsonBox>
            <MolsonTypography sx={{ mb: 3.75 }} variant="h4">
              {t('serviceContract')}
            </MolsonTypography>
            {serviceContract ? (
              <MolsonGrid container={true} spacing={1}>
                <MolsonGrid item={true} xs={6}>
                  <MolsonRadioGroup
                    value={`${
                      serviceContract.hours
                    }hrs (+${formatPriceAndCurrency(
                      serviceContract.dnp as number
                    )})`}
                  >
                    <MolsonRadioButton
                      formControlLabelProps={{
                        label: `${
                          serviceContract.hours
                        }hrs (+${formatPriceAndCurrency(
                          serviceContract.dnp as number
                        )})`
                      }}
                      radioBtnProps={{
                        disabled: true,
                        value: `${
                          serviceContract.hours
                        }hrs (+${formatPriceAndCurrency(
                          serviceContract.dnp as number
                        )})`
                      }}
                      checked={true}
                    />
                  </MolsonRadioGroup>
                </MolsonGrid>
              </MolsonGrid>
            ) : (
              <MolsonGrid container={true} spacing={1}>
                <MolsonGrid item={true} xs={6}>
                  <MolsonRadioGroup value="None">
                    <MolsonRadioButton
                      formControlLabelProps={{
                        label: t('serviceContractNone')
                      }}
                      radioBtnProps={{
                        disabled: true,
                        value: 'None'
                      }}
                      checked={true}
                    />
                  </MolsonRadioGroup>
                </MolsonGrid>
              </MolsonGrid>
            )}
          </MolsonBox>
        )}

        {(transportOption ||
          selectedTransportType === TRANSPORT_OPTION_TYPES.FITTED) && (
          <MolsonBox>
            <MolsonTypography sx={{ mb: 3.75 }} variant="h4">
              {t('transport')}
            </MolsonTypography>
            {transportOption && (
              <MolsonGrid container={true} spacing={1}>
                <MolsonGrid item={true} xs={6}>
                  <MolsonRadioGroup
                    value={`${transportOption.name} (${formatPriceAndCurrency(
                      transportOption.dnp
                    )})`}
                  >
                    <MolsonRadioButton
                      formControlLabelProps={{
                        label: `${
                          transportOption.name
                        } (${formatPriceAndCurrency(transportOption.dnp)})`
                      }}
                      radioBtnProps={{
                        disabled: true,
                        value: `${
                          transportOption.name
                        } (${formatPriceAndCurrency(transportOption.dnp)})`
                      }}
                      checked={true}
                    />
                  </MolsonRadioGroup>
                </MolsonGrid>
              </MolsonGrid>
            )}

            {selectedTransportType === TRANSPORT_OPTION_TYPES.FITTED && (
              <MolsonGrid container={true} spacing={1}>
                <MolsonGrid item={true} xs={6}>
                  <MolsonRadioGroup value={t('transportFitted')}>
                    <MolsonRadioButton
                      formControlLabelProps={{
                        label: t('transportFitted')
                      }}
                      radioBtnProps={{
                        disabled: true,
                        value: t('transportFitted')
                      }}
                      checked={true}
                    />
                  </MolsonRadioGroup>
                </MolsonGrid>
              </MolsonGrid>
            )}
          </MolsonBox>
        )}
      </MolsonBox>
    </MolsonAccordion>
  )
}
